/**
 * @fileoverview Component for displaying a recover password form.
 * This component includes a form for recovering a password and displays a message if the password is incorrect or duplicated.
 */

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { useMemo, useState } from 'react'
import { TextInput } from '../form/TextInput'

/**
 * @interface AccountFormProps
 * @description Props for the AccountForm component.
 * @property {function} onSubmit - The function to call when the form is submitted.
 * @property {boolean} isPending - Whether the form is pending.
 * @property {boolean} isWrongPassword - Whether the password is incorrect.
 * @property {boolean} isDuplicatedPassowrd - Whether the password is duplicated.
 */
interface AccountFormProps {
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
    isPending: boolean
    isWrongPassword: boolean
    isDuplicatedPassowrd: boolean
}

/**
 * @function RecoverPasswordForm
 * @description Component for displaying a recover password form.
 * @param {AccountFormProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the recover password form.
 */
export function RecoverPasswordForm({
    onSubmit,
    isDuplicatedPassowrd,
    isPending,
}: AccountFormProps) {
    const [newPassword, setNewPassword] = useState('')
    const [repeatPassword, setRepeatPassword] = useState('')
    const [isValid, setIsValid] = useState(true)

    const passwordRequirements = useMemo(
        () => [
            {
                test: (pwd: string) => pwd.length >= 8,
                description: 'At least 8 characters',
            },
            {
                test: (pwd: string) => /[a-z]/.test(pwd),
                description: 'At least 1 lowercase character',
            },
            {
                test: (pwd: string) => /[A-Z]/.test(pwd),
                description: 'At least 1 uppercase character',
            },
            {
                test: (pwd: string) => /\d/.test(pwd),
                description: 'At least 1 number',
            },
            {
                test: (pwd: string) => /[!@#$%^&*-]/.test(pwd),
                description: 'At least 1 special character (!@#$%^&*)',
            },
        ],
        []
    )

    function validateForm() {
        return (
            passwordRequirements.every((req) => req.test(newPassword)) &&
            newPassword === repeatPassword
        )
    }

    function submitForm(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        if (validateForm()) {
            onSubmit(e)
        } else {
            setIsValid(false)
        }
    }

    return (
        <form className="relative mt-8 space-y-6" action="#" onSubmit={submitForm}>
            <div className="flex flex-col gap-y-4">
                <div>
                    <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Create password
                    </label>
                    <TextInput
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="password"
                        validationError="Please, fill in your new password"
                        error={isDuplicatedPassowrd}
                        required
                        value={newPassword}
                        onChange={(e) => {
                            setNewPassword(e.target.value)
                        }}
                        className="rounded-md"
                    />
                    <ul className="flex flex-col gap-y-1 mt-2 mb-2 text-sm text-gray-600">
                        {passwordRequirements.map((req, index) => (
                            <li
                                key={index}
                                className={`flex items-center ${
                                    req.test(newPassword) ? 'line-through text-gray-500' : ''
                                }`}
                            >
                                {req.test(newPassword) ? (
                                    <CheckCircleIcon
                                        className="h-5 w-5 -ml-0.5 mr-0.5 text-indigo-500"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <div
                                        className="h-4 w-4 mr-1 border rounded-full border-gray-300 text-white"
                                        aria-hidden="true"
                                    />
                                )}
                                {req.description}
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <label
                        htmlFor="repeatPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Repeat new password
                    </label>
                    <TextInput
                        id="repeatPassword"
                        name="repeatPassword"
                        type="password"
                        autoComplete="password"
                        error={isDuplicatedPassowrd}
                        required
                        validationError="Please, repeat your new password"
                        value={repeatPassword}
                        onChange={(e) => {
                            setRepeatPassword(e.target.value)
                        }}
                        className="rounded-md"
                    />
                </div>

                {!isValid && (
                    <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Passwords do not match.
                                </h3>
                            </div>
                        </div>
                    </div>
                )}

                {isDuplicatedPassowrd && (
                    <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    This password has been used previously. Create a new unique
                                    password that has never been used before.
                                </h3>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div>
                <button
                    type="submit"
                    disabled={isPending}
                    className={`group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-base font-medium text-white disabled:opacity-75 hover:not([disabled]):bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                    Continue
                </button>
            </div>
        </form>
    )
}
